import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../provider/auth';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import MarkdownEditor from '../MarkdownEditor/MarkdownEditor';
import { api_translate } from '../../api/functions';
import { useApi } from '../../api/api';
import './AdminEndpoint.css';

async function geocodeAddress(address) {
  if (!address) {
    console.error('[geocodeAddress] No address provided');
    return null;
  }

  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`;

  try {
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'KehilaTepliceWebsite/1.0'
      }
    });

    if (!response.ok) {
      console.error('[geocodeAddress] HTTP error:', response.status);
      return null;
    }

    const data = await response.json();

    if (data && data.length > 0) {
      const result = data[0];
      const coords = {
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon)
      };
      return coords;
    } else {
      console.error('[geocodeAddress] No results found for address:', address);
      return null;
    }
  } catch (error) {
    console.error('[geocodeAddress] Fetch error:', error);
    return null;
  }
}

const AdminEndpoint = ({
  fields = [],
  onSubmit,
  onCancel,
  initialData = {},
  submitLabel = "Submit",
  isLoading = false,
  storageKey = null
}) => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [translating, setTranslating] = useState({});
  const [fileInputKey, setFileInputKey] = useState(0);
  const skipNextSave = useRef(false);
  const translateApi = useApi(api_translate);

  useEffect(() => {
    const init = {};
    fields.forEach(field => {
      if (field.type === 'language_tabs') {
        field.languages.forEach(lang => {
          field.fields.forEach(subField => {
            const fieldName = `${subField.name}_${lang}`;
            init[fieldName] = initialData[fieldName] || subField.defaultValue || '';
          });
        });
      } else {
        if (field.type === 'boolean') {
          init[field.name] = initialData[field.name] !== undefined ? initialData[field.name] : (field.defaultValue !== undefined ? field.defaultValue : false);
        } else if (field.type === 'number' || field.type === 'select') {
          init[field.name] = initialData[field.name] !== undefined ? initialData[field.name] : (field.defaultValue !== undefined ? field.defaultValue : '');
        } else {
          init[field.name] = initialData[field.name] || field.defaultValue || '';
        }
      }
    });

    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          Object.keys(parsed).forEach(key => {
            if (key in init && initialData[key] === undefined && (!(parsed[key] instanceof Object) || Array.isArray(parsed[key]))) {
              init[key] = parsed[key];
            }
          });
        }
      } catch (e) {}
    }

    setFormData(init);
  }, [initialData, fields, storageKey]);

  useEffect(() => {
    if (!storageKey || Object.keys(formData).length === 0) return;
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }
    try {
      const toSave = {};
      Object.keys(formData).forEach(key => {
        const val = formData[key];
        if (!(val instanceof File) && val !== 'DELETE') {
          toSave[key] = val;
        }
      });
      localStorage.setItem(storageKey, JSON.stringify(toSave));
    } catch (e) {}
  }, [formData, storageKey]);

  const handleClearDraft = () => {
    if (!window.confirm('Clear all fields and discard your draft? This cannot be undone.')) return;
    if (storageKey) localStorage.removeItem(storageKey);
    skipNextSave.current = true;
    const reset = {};
    fields.forEach(field => {
      if (field.type === 'language_tabs') {
        field.languages.forEach(lang => {
          field.fields.forEach(subField => {
            reset[`${subField.name}_${lang}`] = subField.defaultValue || '';
          });
        });
      } else if (field.type === 'file') {
        reset[field.name] = field.currentFile ? 'DELETE' : '';
      } else if (field.type === 'boolean') {
        reset[field.name] = field.defaultValue !== undefined ? field.defaultValue : false;
      } else {
        reset[field.name] = field.defaultValue || '';
      }
    });
    setFileInputKey(k => k + 1);
    setFormData(reset);
  };

  const handleInputChange = (fieldName, value, type) => {
    if (type === 'file') {
      setFormData({ ...formData, [fieldName]: value });
    } else if (type === 'multi_select') {
      setFormData({ ...formData, [fieldName]: value });
    } else if (type === 'boolean') {
      setFormData({ ...formData, [fieldName]: value === 'true' || value === true });
    } else if (type === 'number') {
      setFormData({ ...formData, [fieldName]: parseFloat(value) || 0 });
    } else {
      setFormData({ ...formData, [fieldName]: value });
    }
    if (errors[fieldName]) {
      setErrors({ ...errors, [fieldName]: null });
    }
  };

  const handleFileDelete = (fieldName, currentFileName) => {
    const fileType = fieldName === 'illustration' ? 'image' : 'document';
    const confirmed = window.confirm(
      `Are you sure you want to delete the current ${fileType}?\n\nFile: ${currentFileName}\n\nThis action will be applied when you save the form.`
    );

    if (confirmed) {
      handleInputChange(fieldName, 'DELETE', 'file');
      toast.info(`${fileType.charAt(0).toUpperCase() + fileType.slice(1)} marked for deletion. Save the form to apply changes.`, {
        position: "bottom-right",
        autoClose: 3000
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    fields.forEach(field => {
      if (field.required) {
        const value = formData[field.name];
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          newErrors[field.name] = `${field.label} is required`;
        }
      }
    });

    const startDate = formData['event_start_date'];
    const endDate = formData['event_end_date'];
    const startTime = formData['event_start_time'];
    const endTime = formData['event_end_time'];

    if (startDate && endDate && startTime && endTime) {
      if (endDate < startDate) {
        newErrors['event_end_date'] = 'End date cannot be before start date';
      } else if (endDate === startDate) {
        if (endTime <= startTime) {
          newErrors['event_end_time'] = 'End time must be after start time for same-day events';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      let finalData = { ...formData, token };

      const hasLocationAddress = formData.location_address && formData.location_address.trim();
      const hasLat = formData.location_latitude && formData.location_latitude !== '';
      const hasLng = formData.location_longitude && formData.location_longitude !== '';

      if (hasLocationAddress && (!hasLat || !hasLng)) {
        setIsGeocoding(true);
        try {
          const coords = await geocodeAddress(formData.location_address);
          if (coords) {
            finalData.location_latitude = coords.lat;
            finalData.location_longitude = coords.lng;
          }
        } catch (error) {
          console.error('[AdminEndpoint] Geocoding failed:', error);
        } finally {
          setIsGeocoding(false);
        }
      }

      onSubmit(finalData);
    }
  };

  const [activeLang, setActiveLang] = useState('en');

  const handleTranslate = async (field, sourceLang) => {
    const targetLangs = field.languages.filter(lang => lang !== sourceLang);

    const fieldsWithContent = [];
    for (const subField of field.fields) {
      for (const targetLang of targetLangs) {
        const targetFieldName = `${subField.name}_${targetLang}`;
        if (formData[targetFieldName] && formData[targetFieldName].trim() !== '') {
          fieldsWithContent.push({ field: subField.label, lang: targetLang.toUpperCase() });
        }
      }
    }

    if (fieldsWithContent.length > 0) {
      const fieldsList = fieldsWithContent.map(f => `${f.field} (${f.lang})`).join(', ');
      const confirmed = window.confirm(
        `Some fields already have content: ${fieldsList}\n\n` +
        `Do you want to overwrite them with translations from ${sourceLang.toUpperCase()}?`
      );
      if (!confirmed) {
        return;
      }
    }

    for (const subField of field.fields) {
      const sourceFieldName = `${subField.name}_${sourceLang}`;
      const sourceText = formData[sourceFieldName];

      if (!sourceText || sourceText.trim() === '') {
        continue;
      }

      for (const targetLang of targetLangs) {
        const targetFieldName = `${subField.name}_${targetLang}`;

        setTranslating(prev => ({ ...prev, [targetFieldName]: true }));

        try {
          const sourceLangCode = sourceLang === 'cz' ? 'cs' : sourceLang;
          const targetLangCode = targetLang === 'cz' ? 'cs' : targetLang;

          const result = await translateApi.execute({
            message: sourceText,
            source: sourceLangCode,
            target: targetLangCode
          });

          const translatedText = typeof result === 'string' ? result : result?.translated_text;

          if (translatedText) {
            setFormData(prev => ({
              ...prev,
              [targetFieldName]: translatedText
            }));
          } else {
            console.error(`No translated text in result for ${targetFieldName}:`, result);
          }
        } catch (error) {
          console.error(`Translation failed for ${targetFieldName}:`, error);
        } finally {
          setTranslating(prev => ({ ...prev, [targetFieldName]: false }));
        }
      }
    }
  };

  const renderField = (field) => {
    if (field.type === 'language_tabs') {
      const hasContent = field.fields.some(subField => {
        const fieldName = `${subField.name}_${activeLang}`;
        return formData[fieldName] && formData[fieldName].trim() !== '';
      });

      const isTranslating = Object.keys(translating).some(key =>
        key.includes('_') && translating[key] === true
      );

      return (
        <div key={field.name} className="language-tabs-section">
          <div className="language-tabs-header">
            <div className="language-tabs-buttons">
              {field.languages.map(lang => (
                <button
                  key={lang}
                  type="button"
                  className={`language-tab-btn ${activeLang === lang ? 'active' : ''}`}
                  onClick={() => setActiveLang(lang)}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="translate-btn"
              onClick={() => handleTranslate(field, activeLang)}
              disabled={!hasContent || isTranslating}
              title={`Translate from ${activeLang.toUpperCase()} to other languages`}
            >
              {isTranslating ? (
                <>
                  <span className="translate-spinner"></span>
                  {t('admin.translating')}
                </>
              ) : (
                <>
                  {t('admin.translateToOtherLanguages')}
                </>
              )}
            </button>
          </div>
          <div className="language-tab-content">
            {field.fields.map(subField => {
              const actualFieldName = `${subField.name}_${activeLang}`;
              const isFieldTranslating = translating[actualFieldName];

              const fieldConfig = {
                ...subField,
                name: actualFieldName,
                label: subField.label,
                disabled: isFieldTranslating
              };
              return (
                <div key={actualFieldName} style={{ position: 'relative' }}>
                  {renderField(fieldConfig)}
                  {isFieldTranslating && (
                    <div className="field-translating-overlay">
                      <span className="translate-spinner"></span>
                      {t('admin.translating')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (field.type === 'multi_select') {
      const selectedValues = Array.isArray(formData[field.name]) ? formData[field.name] : [];
      const hasError = errors[field.name];
      return (
        <div key={field.name} className={`admin-form-field ${field.className || ''}`}>
          <label className="admin-form-label">
            {field.label}
            {field.required && <span className="required-indicator">*</span>}
          </label>
          <div className="admin-multi-select">
            {field.options && field.options.map(option => (
              <label key={option.value} className="admin-multi-select-option">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...selectedValues, option.value]
                      : selectedValues.filter(v => v !== option.value);
                    handleInputChange(field.name, newValues, 'multi_select');
                  }}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
          {hasError && <span className="error-message">{hasError}</span>}
        </div>
      );
    }

    const value = formData[field.name] ?? '';
    const hasError = errors[field.name];

    if (field.type === 'boolean') {
      return (
        <label key={field.name} className={`admin-form-field admin-form-field--boolean ${field.className || ''}`}>
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => handleInputChange(field.name, e.target.checked, 'boolean')}
            disabled={field.disabled}
            className="admin-boolean-checkbox"
          />
          <span className="admin-form-label" style={{ marginTop: 0, marginBottom: 0 }}>
            {field.label}
            {field.required && <span className="required-indicator">*</span>}
          </span>
        </label>
      );
    }

    return (
      <div key={field.name} className={`admin-form-field ${field.className || ''}`}>
        <label className="admin-form-label">
          {field.label}
          {field.required && <span className="required-indicator">*</span>}
        </label>

        {field.type === 'file' ? (
          <div className="admin-file-input">
            <input
              key={fileInputKey}
              type="file"
              accept={field.accept || '*'}
              onChange={(e) => handleInputChange(field.name, e.target.files[0], 'file')}
              className={hasError ? 'error' : ''}
            />
            {field.currentFile && formData[field.name] !== 'DELETE' && (
              <div className="current-file-info">
                <span className="current-file-name">Current: {field.currentFile}</span>
                <button
                  type="button"
                  className="delete-file-btn"
                  onClick={() => handleFileDelete(field.name, field.currentFile)}
                  title="Delete current file"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t('admin.delete')}
                </button>
              </div>
            )}
          </div>
        ) : field.type === 'select' ? (
          <select
            value={value}
            onChange={(e) => handleInputChange(field.name, parseInt(e.target.value) || e.target.value, field.valueType || 'number')}
            className={hasError ? 'error' : ''}
            disabled={field.disabled}
          >
            <option value="">-- Select {field.label} --</option>
            {field.options && field.options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : field.type === 'markdown' ? (
          <MarkdownEditor
            value={value}
            onChange={(val) => handleInputChange(field.name, val)}
            placeholder={field.placeholder}
          />
        ) : field.type === 'textarea' ? (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            rows={field.rows || 4}
            className={hasError ? 'error' : ''}
          />
        ) : field.type === 'date' ? (
          <input
            type="date"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className={hasError ? 'error' : ''}
            pattern="\d{4}-\d{2}-\d{2}"
          />
        ) : field.type === 'time' ? (
          <input
            type="time"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className={hasError ? 'error' : ''}
            step="60"
          />
        ) : (
          <input
            type={field.type || 'text'}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value, field.type)}
            placeholder={field.placeholder}
            className={hasError ? 'error' : ''}
            step={field.type === 'number' ? field.step || '0.01' : undefined}
          />
        )}

        {hasError && <span className="error-message">{hasError}</span>}
        {field.hint && <span className="field-hint">{field.hint}</span>}
      </div>
    );
  };

  return (
    <form className="admin-form" onSubmit={handleSubmit}>
      <div className="admin-form-fields">
        {fields.map(field => renderField(field))}
      </div>

      <div className="admin-form-actions">
        {onCancel && (
          <button
            type="button"
            className="admin-btn admin-btn-cancel"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
        )}
        {storageKey && (
          <button
            type="button"
            className="admin-btn admin-btn-clear"
            onClick={handleClearDraft}
            disabled={isLoading}
          >
            Clear
          </button>
        )}
        <button
          type="submit"
          className="admin-btn admin-btn-submit"
          disabled={isLoading || isGeocoding}
        >
          {isGeocoding ? 'Getting coordinates...' : (isLoading ? 'Saving...' : submitLabel)}
        </button>
      </div>
    </form>
  );
};

export default AdminEndpoint;
